import { BackgroundCheckError, countRequestsByType } from '@/backend/features/background-check';
import { BackgroundCheckConfig, BackgroundCheckQuota } from '@/backend/features/background-check/defaults';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import {
    BackgroundCheckStatus,
    BackgroundCheckType,
    intellicorpBackgroundCheckTypes,
    jnetBackgroundCheckTypes,
} from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { AppContext } from '@juvare/platform-app/App';
import { AppState } from '@juvare/platform-sdk-react/types';

export const commonBackgroundCheckRequestValidation = ({
    backgroundCheckType,
    config,
    quota,
    backgroundCheck,
    context,
}: {
    context: AppContext<AppState>;
    backgroundCheck: BackgroundCheckColumns;
    backgroundCheckType: BackgroundCheckType;
    config: BackgroundCheckConfig;
    quota: BackgroundCheckQuota;
}) => {
    const configByType = backgroundCheckType === 'jnet' ? config.type.jnet : config.type[backgroundCheckType];
    const jasType =
        backgroundCheckType === 'jnet'
            ? jnetBackgroundCheckTypes.jnet.jasType
            : intellicorpBackgroundCheckTypes[backgroundCheckType]?.jasType;

    if (!jasType) {
        throw new BackgroundCheckError(`Background check is not enabled for ${configByType?.label} type`, 'processingError');
    }

    if (!configByType.enabled) {
        throw new BackgroundCheckError(`Background check is not enabled for ${configByType?.label} type`, 'processingError');
    }

    if (quota[backgroundCheckType].enabled && quota[backgroundCheckType].quota <= countRequestsByType(context, backgroundCheckType)) {
        throw new BackgroundCheckError('Background check request limit reached', backgroundCheck.status as BackgroundCheckStatus);
    }

    if (backgroundCheck.status === 'didNotConsent' || !backgroundCheck.consent) {
        throw new BackgroundCheckError('User Did Not Consent To Background Check', backgroundCheck.status as BackgroundCheckStatus);
    }

    if (backgroundCheck.status === 'formIncomplete') {
        throw new BackgroundCheckError('Form is incomplete', backgroundCheck.status as BackgroundCheckStatus);
    }

    if (backgroundCheck.status === 'processingSubmitted') {
        throw new BackgroundCheckError('Background check is already in progress', backgroundCheck.status as BackgroundCheckStatus);
    }
};

export const checkFirstName = (accountData: ExtendedAccountData) => {
    if (!accountData.firstName) {
        throw new BackgroundCheckError('First Name is required', 'missingFields');
    }
};

export const checkLastName = (accountData: ExtendedAccountData) => {
    if (!accountData.lastName) {
        throw new BackgroundCheckError('Last Name is required', 'missingFields');
    }
};

export const checkDateOfBirth = (accountData: ExtendedAccountData) => {
    if (!accountData.dateOfBirth) {
        throw new BackgroundCheckError('Date of birth is required', 'missingFields');
    }
};

export const checkAddress = (accountData: ExtendedAccountData) => {
    if (!accountData.address) {
        throw new BackgroundCheckError('Address is required', 'missingFields');
    }
};

export const checkAddressLine1 = (accountData: ExtendedAccountData) => {
    if (!accountData?.address?.addressLine1) {
        throw new BackgroundCheckError('Address Line 1 is required', 'missingFields');
    }
};

export const checkCity = (accountData: ExtendedAccountData) => {
    if (!accountData?.address?.city) {
        throw new BackgroundCheckError('City is required', 'missingFields');
    }
};

export const checkState = (accountData: ExtendedAccountData) => {
    if (!accountData?.address?.state) {
        throw new BackgroundCheckError('State is required', 'missingFields');
    }
};

export const checkStateOfWork = (accountData: ExtendedAccountData) => {
    if (!accountData?.stateOfWork && !accountData?.address?.state) {
        throw new BackgroundCheckError('State of Work is required', 'missingFields');
    }
};

export const checkPostalCode = (accountData: ExtendedAccountData) => {
    if (!accountData?.address?.postalCode) {
        throw new BackgroundCheckError('ZIP code is required', 'missingFields');
    }
};

export const checkGender = (accountData: ExtendedAccountData) => {
    if (!accountData.genderId) {
        throw new BackgroundCheckError('Gender is required', 'missingFields');
    }
};

export const checkSsn = (accountData: ExtendedAccountData) => {
    if (!accountData.ssn) {
        throw new BackgroundCheckError('SSN is required', 'missingFields');
    }
};
